import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import Header from '../components/Header';
import {
  Rocket, Check, Loader2, Sparkles, Clock, BookOpen, Zap, ArrowRight, Crown, Star,
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const loadRazorpayScript = () =>
  new Promise((resolve) => {
    if (window.Razorpay) return resolve(true);
    const s = document.createElement('script');
    s.src = 'https://checkout.razorpay.com/v1/checkout.js';
    s.onload = () => resolve(true);
    s.onerror = () => resolve(false);
    document.body.appendChild(s);
  });

const FEATURES = [
  { icon: Clock,     text: '15 days · ~90 minutes/day · high-yield only' },
  { icon: BookOpen,  text: 'Physics + Chemistry + Maths/Biology every day' },
  { icon: Sparkles,  text: 'AI-generated formula sheets + PYQ drills' },
  { icon: Zap,       text: 'Chapter mastery quizzes with instant feedback' },
  { icon: Star,      text: '30-day full access from purchase date' },
];

const CrashCourse = () => {
  const navigate = useNavigate();
  const [product, setProduct] = useState(null);
  const [track, setTrack] = useState('JEE');
  const [paying, setPaying] = useState(false);

  useEffect(() => {
    axios.get(`${API_URL}/api/crash-course/product`)
      .then(({ data }) => setProduct(data))
      .catch(() => toast.error('Failed to load product'));
  }, []);

  const handleBuy = async () => {
    if (!product) return;
    if (product.user_access?.[track]) {
      toast.info(`You already have active ${track} crash-course access.`);
      navigate('/courses');
      return;
    }
    setPaying(true);
    try {
      const ok = await loadRazorpayScript();
      if (!ok) { toast.error('Razorpay failed to load'); setPaying(false); return; }
      const { data } = await axios.post(`${API_URL}/api/crash-course/create-order`, { track });
      const options = {
        key: data.key_id,
        amount: data.amount,
        currency: data.currency,
        order_id: data.order_id,
        name: 'Aptivon Crash Course',
        description: `${track} 15-Day Crash Course — ₹${product.amount_inr}`,
        prefill: { name: data.user.name, email: data.user.email },
        theme: { color: '#EF4444' },
        handler: async (response) => {
          try {
            await axios.post(`${API_URL}/api/crash-course/verify`, {
              razorpay_order_id: response.razorpay_order_id,
              razorpay_payment_id: response.razorpay_payment_id,
              razorpay_signature: response.razorpay_signature,
              track,
            });
            toast.success(`🎉 ${track} crash course unlocked!`);
            navigate('/courses');
          } catch (err) {
            toast.error(err.response?.data?.detail || 'Verification failed');
          }
        },
        modal: { ondismiss: () => setPaying(false) },
      };
      const rzp = new window.Razorpay(options);
      rzp.on('payment.failed', () => { toast.error('Payment failed'); setPaying(false); });
      rzp.open();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to start checkout');
      setPaying(false);
    }
  };

  if (!product) {
    return (
      <div className="min-h-screen"><Header />
        <div className="flex items-center justify-center py-32">
          <Loader2 className="w-8 h-8 animate-spin" style={{ color: 'rgb(var(--accent))' }} />
        </div>
      </div>
    );
  }

  const hasAccess = product.user_access?.[track];

  return (
    <div className="min-h-screen" data-testid="crash-course-page">
      <Header />
      <main className="max-w-[1100px] mx-auto px-4 sm:px-6 py-6 sm:py-10">

        <div className="mb-8 motion-fade-up">
          <div className="flex items-center gap-2 text-[11px] uppercase tracking-widest text-fg-faint font-medium mb-2">
            <Rocket className="w-3 h-3" style={{ color: 'rgb(var(--accent))' }} />
            <span>15-Day Crash Course · Standalone Pass</span>
          </div>
          <h1 className="text-4xl sm:text-5xl font-bold text-fg tracking-tight mb-3">
            Sprint to <span style={{ color: 'rgb(var(--accent))' }}>97+ percentile</span> in 15 days.
          </h1>
          <p className="text-sm sm:text-base text-fg-muted max-w-2xl">
            No monthly commitment. Buy the crash-course once for ₹{product.amount_inr}, get {product.access_days} days of access to the high-yield JEE or NEET revision sprint.
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-[1fr_420px] gap-6">

          {/* Left: pitch */}
          <div
            className="rounded-xl border p-6 sm:p-8 motion-fade-up motion-delay-1"
            style={{ background: 'rgb(var(--card))', borderColor: 'rgb(var(--border))' }}
            data-testid="crash-course-features"
          >
            <p className="text-[10px] font-medium uppercase tracking-widest text-fg-faint mb-4">What you get</p>
            <ul className="space-y-4">
              {FEATURES.map(({ icon: Icon, text }, i) => (
                <li key={i} className="flex items-start gap-3">
                  <div
                    className="w-9 h-9 rounded-md flex items-center justify-center shrink-0"
                    style={{ background: 'rgba(239,68,68,0.12)' }}
                  >
                    <Icon className="w-4 h-4" style={{ color: 'rgb(var(--accent))' }} />
                  </div>
                  <div className="pt-1 text-sm text-fg-secondary">{text}</div>
                </li>
              ))}
            </ul>

            <div className="mt-6 pt-6 border-t" style={{ borderColor: 'rgb(var(--border))' }}>
              <p className="text-[10px] font-medium uppercase tracking-widest text-fg-faint mb-2">Alternatively</p>
              <div className="flex items-start gap-3 p-4 rounded-md" style={{ background: 'rgb(var(--card-hover))' }}>
                <Crown className="w-4 h-4 shrink-0 mt-0.5" style={{ color: '#F59E0B' }} />
                <div className="flex-1">
                  <p className="text-sm font-semibold text-fg">Prefer everything unlocked?</p>
                  <p className="text-xs text-fg-muted mt-0.5">Pro (from ₹7/day) includes the crash course + unlimited AI quizzes, weakness reports and monthly points refill.</p>
                </div>
                <button
                  onClick={() => navigate('/upgrade')}
                  className="shrink-0 text-xs font-semibold text-fg-muted hover:text-fg transition"
                  data-testid="crash-see-pro-btn"
                >
                  See Pro →
                </button>
              </div>
            </div>
          </div>

          {/* Right: checkout card */}
          <div
            className="rounded-xl border p-6 relative overflow-hidden motion-fade-up motion-delay-2"
            style={{
              background: 'linear-gradient(135deg, rgb(var(--card)) 0%, rgba(239,68,68,0.08) 100%)',
              borderColor: 'rgba(239,68,68,0.35)',
            }}
            data-testid="crash-course-checkout"
          >
            <div className="absolute -top-20 -right-20 w-64 h-64 rounded-full blur-3xl pointer-events-none"
              style={{ background: 'rgba(239,68,68,0.18)' }} />
            <div className="relative">
              <p className="text-[10px] font-medium uppercase tracking-widest text-fg-faint mb-2">Pick your track</p>
              <div className="flex rounded-md border p-1 mb-5" style={{ background: 'rgb(var(--card))', borderColor: 'rgb(var(--border))' }} data-testid="crash-track-toggle">
                {['JEE', 'NEET'].map(t => (
                  <button
                    key={t}
                    onClick={() => setTrack(t)}
                    data-testid={`crash-track-${t.toLowerCase()}`}
                    className={`flex-1 px-4 py-2 rounded text-sm font-semibold transition ${track === t ? 'text-white' : 'text-fg-muted hover:text-fg'}`}
                    style={track === t ? { background: 'rgb(var(--accent))' } : {}}
                  >
                    {t}
                  </button>
                ))}
              </div>

              <p className="text-[10px] font-medium uppercase tracking-widest text-fg-faint">One-time purchase</p>
              <div className="flex items-baseline gap-2 mt-1">
                <span className="text-5xl font-bold text-fg tracking-tight">₹{product.amount_inr}</span>
                <span className="text-sm text-fg-muted">/ pass</span>
              </div>
              <p className="text-xs text-fg-muted mt-1 mb-6">One-time · No auto-renewal · {product.access_days}-day access</p>

              {hasAccess ? (
                <div
                  className="p-4 rounded-md flex items-center gap-3 mb-3"
                  style={{ background: 'rgba(16,185,129,0.10)', border: '1px solid rgba(16,185,129,0.35)' }}
                  data-testid="crash-already-owned"
                >
                  <Check className="w-4 h-4" style={{ color: 'rgb(16,185,129)' }} />
                  <p className="text-sm font-semibold text-fg">You own the {track} crash-course</p>
                </div>
              ) : null}

              <button
                onClick={handleBuy}
                disabled={paying}
                className="w-full inline-flex items-center justify-center gap-2 px-4 py-3.5 rounded-md text-sm font-semibold text-white transition hover:brightness-110 disabled:opacity-60"
                style={{ background: 'rgb(var(--accent))' }}
                data-testid="crash-buy-btn"
              >
                {paying ? (
                  <><Loader2 className="w-4 h-4 animate-spin" /> Opening checkout…</>
                ) : hasAccess ? (
                  <>Go to course <ArrowRight className="w-4 h-4" /></>
                ) : (
                  <><Rocket className="w-4 h-4" /> Buy {track} pass · ₹{product.amount_inr}</>
                )}
              </button>
              <p className="mt-3 text-[11px] text-fg-faint text-center">
                Secure UPI · Cards · NetBanking via Razorpay
              </p>
            </div>
          </div>
        </div>
      </main>
    </div>
  );
};

export default CrashCourse;
